import type { AISessionSseEvent } from '@/types/ai-session';

import {
  compareUnsignedDecimal,
  incrementUnsignedDecimal,
  parseOrderedSseEventId,
} from '@/lib/ai-session/protocol/sse-event-id';

export const AI_SESSION_SSE_SUPPORTED_SCHEMA_VERSIONS = new Set([1]);

const EPHEMERAL_SSE_EVENTS = new Set(['token', 'heartbeat']);

export type DurableSseDecision = 'apply' | 'drop' | 'snapshot';

export type DurableSseState = {
  lastPersistentByRun: Map<string, string>;
  seenIds: Set<string>;
};

export function createDurableSseState(initialEventId?: string | null): DurableSseState {
  const state: DurableSseState = {
    lastPersistentByRun: new Map(),
    seenIds: new Set(),
  };
  if (initialEventId) {
    state.seenIds.add(initialEventId);
    const parsed = parseOrderedSseEventId(initialEventId);
    if (parsed?.format === 'persistent') {
      state.lastPersistentByRun.set(parsed.runId, parsed.sequence);
    }
  }
  return state;
}

function readSchemaVersion(event: AISessionSseEvent): number | null {
  const raw = event.data.schemaVersion;
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
}

/** 读取同 run 的前驱序号；缺失或跨 run 时返回 null，交回旧的严格连续判定。 */
function readPrevSequence(event: AISessionSseEvent, runId: string): string | null {
  if (!event.prevId) return null;
  const prev = parseOrderedSseEventId(event.prevId);
  if (!prev || prev.format !== 'persistent' || prev.runId !== runId) return null;
  return prev.sequence;
}

export function hasUnsupportedDurableSseSchema(event: AISessionSseEvent): boolean {
  const schemaVersion = readSchemaVersion(event);
  return schemaVersion !== null && !AI_SESSION_SSE_SUPPORTED_SCHEMA_VERSIONS.has(schemaVersion);
}

export function decideDurableSseEvent(
  state: DurableSseState,
  event: AISessionSseEvent
): DurableSseDecision {
  if (hasUnsupportedDurableSseSchema(event)) {
    return 'snapshot';
  }

  const eventId = event.id;
  if (eventId && state.seenIds.has(eventId)) {
    return 'drop';
  }

  if (EPHEMERAL_SSE_EVENTS.has(event.event)) {
    if (eventId) state.seenIds.add(eventId);
    return 'apply';
  }

  if (!eventId) {
    return 'apply';
  }

  const parsed = parseOrderedSseEventId(eventId);
  if (parsed?.format === 'persistent') {
    const lastSequence = state.lastPersistentByRun.get(parsed.runId);
    const prevSequence = readPrevSequence(event, parsed.runId);
    if (prevSequence !== null) {
      // 服务端给出因果前驱：sequence 会被「占号但不投影到 wire」的事件跳过，
      // 也会被回放栅栏抬高，只有前驱超过已应用序号才是真断档。
      if (lastSequence) {
        if (compareUnsignedDecimal(prevSequence, lastSequence) > 0) {
          return 'snapshot';
        }
        if (compareUnsignedDecimal(parsed.sequence, lastSequence) <= 0) {
          return 'drop';
        }
      }
    } else if (!lastSequence) {
      // 旧后端无前驱：持久事件从 1 开始。首次收到更大的 sequence 说明连接前已有
      // 事件丢失，不能直接把中间态当成完整状态应用。
      if (compareUnsignedDecimal(parsed.sequence, '1') !== 0) {
        return 'snapshot';
      }
    } else {
      if (compareUnsignedDecimal(parsed.sequence, lastSequence) <= 0) {
        return 'drop';
      }
      const expected = incrementUnsignedDecimal(lastSequence);
      if (compareUnsignedDecimal(parsed.sequence, expected) > 0) {
        return 'snapshot';
      }
    }
    state.seenIds.add(eventId);
    state.lastPersistentByRun.set(parsed.runId, parsed.sequence);
    return 'apply';
  }

  state.seenIds.add(eventId);
  return 'apply';
}
